import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import type {
  CreateHighFiveDto,
  HighFiveLeaderboardDto,
  HighFiveLeaderboardRowDto,
  HighFiveRecipientOptionDto,
  HighFiveWeekDto,
} from '../types';
import { formatDate, formatDateRange } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotSubmit } from '../utils/userFacingCopy';
import { useSuccessSnackbar } from '../contexts/SuccessSnackbarContext';
import { successCopy } from '../utils/successCopy';

interface Option {
  id: string;
  label: string;
}

type LeaderboardMetric = 'allTime' | 'lastPublishedWeek';

interface LeaderboardEntryView {
  row: HighFiveLeaderboardRowDto;
  displayRank: number;
}

function rankMedalOrNumber(rank: number): string {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
}

function HighFivesPodiumStrip({
  entries,
  metric,
  currentUserId,
}: {
  entries: LeaderboardEntryView[];
  metric: LeaderboardMetric;
  currentUserId: string | undefined;
}) {
  if (entries.length === 0) return null;

  const slotCard = (entry: LeaderboardEntryView, tier: 1 | 2 | 3) => {
    const isMe =
      !!currentUserId && entry.row.userId.toLowerCase() === currentUserId.toLowerCase();
    const shadow = tier === 1 ? 6 : tier === 2 ? 4 : 3;
    const pt = tier === 1 ? 2.5 : tier === 2 ? 2 : 1.75;
    return (
      <Card
        key={entry.row.userId}
        variant="outlined"
        sx={(theme) => ({
          flex: 1,
          maxWidth: 220,
          minWidth: 0,
          boxShadow: theme.shadows[shadow],
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[Math.min(shadow + 2, 24)],
          },
          pt,
          pb: 2,
          px: 0.5,
          ...(isMe && {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderColor: 'primary.main',
            borderWidth: 2,
          }),
        })}
      >
        <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1, '&:last-child': { pb: 1.5 } }}>
          <Typography sx={{ fontSize: '1.75rem', lineHeight: 1 }} aria-hidden>
            {rankMedalOrNumber(entry.displayRank)}
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ mt: 0.75, wordBreak: 'break-word', lineHeight: 1.25 }}
          >
            {entry.row.userName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {metric === 'allTime'
              ? `${entry.row.totalHighFives} total high fives`
              : `${entry.row.highFivesThisPublishedWeek ?? 0} in last published week`}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  if (entries.length === 1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
        <Box sx={{ width: '100%', maxWidth: 280 }}>{slotCard(entries[0], 1)}</Box>
      </Box>
    );
  }

  if (entries.length === 2) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          gap: 1.5,
          mb: 2.5,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        {slotCard(entries[1], 2)}
        {slotCard(entries[0], 1)}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        gap: 1.5,
        mb: 2.5,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      {slotCard(entries[1], 2)}
      {slotCard(entries[0], 1)}
      {slotCard(entries[2], 3)}
    </Box>
  );
}

export default function HighFivesPage() {
  const { user } = useAuth();
  const showSuccess = useSuccessSnackbar();
  const isAdmin = (user?.roles ?? []).includes('Administrator');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [week, setWeek] = useState<HighFiveWeekDto | null>(null);
  const [leaderboard, setLeaderboard] = useState<HighFiveLeaderboardDto | null>(null);
  const [users, setUsers] = useState<Option[]>([]);
  const [form, setForm] = useState<CreateHighFiveDto>({
    recipientId: '',
    reasonContext: '',
  });
  const [leaderboardMetric, setLeaderboardMetric] = useState<LeaderboardMetric>('allTime');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requests: Promise<unknown>[] = [
        apiService.get<HighFiveLeaderboardDto>(API_ENDPOINTS.HIGH_FIVES.LEADERBOARD),
        apiService.get<HighFiveRecipientOptionDto[]>(API_ENDPOINTS.HIGH_FIVES.RECIPIENT_OPTIONS),
      ];
      if (isAdmin) {
        requests.unshift(apiService.get<HighFiveWeekDto>(API_ENDPOINTS.HIGH_FIVES.CURRENT_WEEK));
      }

      const results = await Promise.all(requests);
      const weekRes = isAdmin ? (results[0] as Awaited<ReturnType<typeof apiService.get<HighFiveWeekDto>>>) : null;
      const leaderboardRes = (results[isAdmin ? 1 : 0] as Awaited<ReturnType<typeof apiService.get<HighFiveLeaderboardDto>>>);
      const usersRes = (results[isAdmin ? 2 : 1] as Awaited<ReturnType<typeof apiService.get<HighFiveRecipientOptionDto[]>>>);

      setWeek(weekRes?.data ?? null);
      setLeaderboard(leaderboardRes.data);
      setUsers(
        (usersRes.data ?? [])
          .map((u) => ({ id: u.userId, label: u.fullName }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotLoad('high fives')));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  const canSubmit = useMemo(() => {
    const hasValidFields =
      form.recipientId.trim().length > 0 &&
      form.reasonContext.trim().length >= 10;
    if (!hasValidFields) return false;
    if (!isAdmin) return true;
    return week?.status === 'Draft';
  }, [form, isAdmin, week?.status]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      await apiService.post(API_ENDPOINTS.HIGH_FIVES.BASE, form);
      setForm({ recipientId: '', reasonContext: '' });
      showSuccess(successCopy.highFiveSavedToWeek);
      await load();
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotSubmit('your high five')));
    } finally {
      setSaving(false);
    }
  };

  const movementIcon = (movementType: string) => {
    if (movementType === 'up') return <ArrowUpwardIcon fontSize="small" color="success" />;
    if (movementType === 'down') return <ArrowDownwardIcon fontSize="small" color="error" />;
    if (movementType === 'new') return <FiberManualRecordIcon fontSize="small" color="primary" />;
    return null;
  };

  const movementLabel = (movementType: string, movement: number) => {
    if (movementType === 'new') return '(new)';
    if (movement === 0) return '';
    return `(${movement > 0 ? '+' : ''}${movement})`;
  };

  const movementTextColor = (movementType: string) => {
    if (movementType === 'up') return 'success.main';
    if (movementType === 'down') return 'error.main';
    return 'text.secondary';
  };

  const leaderboardEntries = useMemo((): LeaderboardEntryView[] => {
    const rows = leaderboard?.rows;
    if (!rows?.length) return [];
    if (leaderboardMetric === 'allTime') {
      return rows.map((r) => ({ row: r, displayRank: r.rank }));
    }
    // lastPublishedWeek: counts from the snapshot’s published week only (not the admin-only draft week).
    const sorted = [...rows].sort((a, b) => {
      const aw = a.highFivesThisPublishedWeek ?? 0;
      const bw = b.highFivesThisPublishedWeek ?? 0;
      if (bw !== aw) return bw - aw;
      return a.userName.localeCompare(b.userName);
    });
    return sorted.map((r, i) => ({ row: r, displayRank: i + 1 }));
  }, [leaderboard?.rows, leaderboardMetric]);

  const podiumEntries = useMemo(() => leaderboardEntries.slice(0, 3), [leaderboardEntries]);
  const listEntries = useMemo(() => leaderboardEntries.slice(3), [leaderboardEntries]);

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <EmojiEventsOutlinedIcon sx={{ color: 'primary.main', fontSize: 32, alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            High Fives
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>Give a high five</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Recognition for this week. Context is required so HR can announce clearly on Friday.
                </Typography>
                <Stack spacing={2}>
                  <Autocomplete
                    options={users}
                    value={users.find((u) => u.id === form.recipientId) ?? null}
                    onChange={(_, option) => setForm((prev) => ({ ...prev, recipientId: option?.id ?? '' }))}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label="Recipient" placeholder="Search by name..." />}
                  />
                  <TextField
                    label="Context (required)"
                    value={form.reasonContext}
                    onChange={(e) => setForm((prev) => ({ ...prev, reasonContext: e.target.value }))}
                    multiline
                    minRows={3}
                    helperText={`${form.reasonContext.length}/1000 characters`}
                    inputProps={{ maxLength: 1000 }}
                  />
                  <Button variant="contained" disabled={!canSubmit || saving} onClick={handleSubmit}>
                    {saving ? 'Saving...' : 'Submit high five'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {isAdmin && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">This week</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {week ? `${formatDate(week.weekStartDate)} - ${formatDate(week.weekEndDate)}` : 'No week'}
                    </Typography>
                  </Box>
                  {week?.status === 'Published' && (
                    <Alert severity="info" sx={{ mb: 2 }}>This week has already been published.</Alert>
                  )}
                  {week?.status === 'Draft' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This week publishes automatically on Friday at 12:00 (organisation time), then HR sends the weekly high-fives email.
                    </Alert>
                  )}
                  {week?.highFives?.length ? (
                    <List dense>
                      {week.highFives.map((item) => (
                        <ListItem key={item.id} divider>
                          <ListItemText
                            primary={`${item.senderName} -> ${item.recipientName}`}
                            secondary={item.reasonContext}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No high fives yet for this week.</Typography>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: leaderboard?.publishedAtUtc ? 0.5 : 1 }}>Leaderboard</Typography>
                {leaderboard?.publishedAtUtc ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Published at {formatDate(leaderboard.publishedAtUtc, { includeTime: true })}
                  </Typography>
                ) : null}
                {leaderboardEntries.length ? (
                  <>
                    <Tabs
                      value={leaderboardMetric}
                      onChange={(_, value) => setLeaderboardMetric(value)}
                      aria-label="Leaderboard ranking basis"
                      sx={{
                        mb: 1,
                        minHeight: 40,
                        '& .MuiTab-root': { textTransform: 'none', minHeight: 40 },
                      }}
                    >
                      <Tab label="All-time" value="allTime" />
                      <Tab label="Last published week" value="lastPublishedWeek" />
                    </Tabs>
                    {leaderboardMetric === 'lastPublishedWeek' && leaderboard ? (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        Who received the most recognition during{' '}
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {formatDateRange(leaderboard.weekStartDate, leaderboard.weekEndDate)}
                        </Box>
                        ? Use <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>All-time</Box> for
                        overall standings and how ranks changed.
                      </Typography>
                    ) : null}
                    <HighFivesPodiumStrip
                      entries={podiumEntries}
                      metric={leaderboardMetric}
                      currentUserId={user?.id}
                    />
                    {listEntries.length ? (
                      <>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>
                          Other ranks
                        </Typography>
                        <List dense>
                          {listEntries.map(({ row, displayRank }) => {
                            const isCurrentUser =
                              !!user?.id && row.userId.toLowerCase() === user.id.toLowerCase();
                            const showMovement = leaderboardMetric === 'allTime';
                            return (
                              <ListItem
                                key={row.userId}
                                divider
                                sx={(theme) => ({
                                  borderRadius: 1,
                                  '&:last-child': { borderBottom: 'none' },
                                  ...(isCurrentUser && {
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
                                  }),
                                })}
                              >
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      fontWeight={600}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                      aria-label={
                                        isCurrentUser
                                          ? `Your rank: ${displayRank}, ${row.userName}`
                                          : `Rank ${displayRank}, ${row.userName}`
                                      }
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          minWidth: displayRank <= 3 ? 28 : undefined,
                                          fontSize: displayRank <= 3 ? '1.35rem' : 'inherit',
                                          lineHeight: 1,
                                        }}
                                      >
                                        {rankMedalOrNumber(displayRank)}
                                      </Box>
                                      <span>{row.userName}</span>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {leaderboardMetric === 'allTime'
                                        ? `${row.totalHighFives} total high fives`
                                        : `${row.highFivesThisPublishedWeek ?? 0} in last published week`}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 74, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                    {showMovement ? (
                                      <>
                                        <Typography variant="body2" sx={{ minWidth: 36, textAlign: 'right', color: movementTextColor(row.movementType) }}>
                                          {movementLabel(row.movementType, row.movement)}
                                        </Typography>
                                        {movementIcon(row.movementType) ? (
                                          <Box sx={{ width: 18, display: 'flex', justifyContent: 'center' }}>
                                            {movementIcon(row.movementType)}
                                          </Box>
                                        ) : (
                                          <Box sx={{ width: 18 }} />
                                        )}
                                      </>
                                    ) : (
                                      <Box sx={{ width: 18 }} />
                                    )}
                                  </Box>
                                </Box>
                              </ListItem>
                            );
                          })}
                        </List>
                      </>
                    ) : null}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Leaderboard will appear after the first week is published.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </AppLayout>
  );
}
